import { TypeUtils } from '@creejs/commons-lang'

const { isNumber, isString } = TypeUtils
/**
 * @namespace LogLevel
 * @description Defines the logging levels and provides utilities for working with them.
 */
// module vars
const TRACE = 'TRACE'
const DEBUG = 'DEBUG'
const INFO = 'INFO'
const WARN = 'WARN'
const ERROR = 'ERROR'
const FATAL = 'FATAL'
const OFF = 'OFF'
const ALL = 'ALL'

const Names = new Set([TRACE, DEBUG, INFO, WARN, ERROR, FATAL, OFF, ALL])
/**
 * The logging level numbers, where OFF=-1, FATAL=0, ERROR=1, WARN=2, INFO=3, DEBUG=4, TRACE=5, ALL=6
 * @enum {number}
 * @memberof LogLevel
 * @static
 */
const Level = {
  OFF: -1,
  FATAL: 0,
  ERROR: 1,
  WARN: 2,
  INFO: 3,
  DEBUG: 4,
  TRACE: 5,
  ALL: 6
}

/**
 * Standard logging level names
 * @enum {string}
 * @memberof LogLevel
 * @static
 * @constant
 */
const Name = {
  TRACE,
  DEBUG,
  INFO,
  WARN,
  ERROR,
  FATAL,
  OFF
}

/**
 * Internal map for quick lookup of level names by their numeric values
 * @type {Map<number, string>}
 * @private
 */
const Value2Name = new Map()
for (const [name, value] of Object.entries(Level)) {
  Value2Name.set(value, name)
}

/**
 * The default logging level (ERROR)
 * @type {number}
 * @memberof LogLevel
 * @static
 */
const DefaultLevel = Level.ERROR

/**
 * Converts a numeric logging level value to its corresponding name.
 * @param {number} value - The numeric logging level value to convert.
 * @returns {string|undefined} The name associated with the given logging level value, from {@link LogLevel.Name}.
 * @memberof LogLevel
 * @static
 */
function value2Name (value) {
  if (!isNumber(value)) {
    return undefined
  }
  return Value2Name.get(value)
}

/**
 * Converts a logging level name to its corresponding numeric value.
 * @param {string} name - The name of the logging level.
 * @returns {number|undefined} The numeric value of the logging level from {@link LogLevel.Level}, or undefined if name is not supported.
 * @memberof LogLevel
 * @static
 */
function name2Value (name) {
  if (!isString(name)) {
    return undefined
  }
  // @ts-ignore
  return Level[name.toUpperCase()]
}

/**
 * Checks if the given level is a valid log level.
 * @param {number} level - The log level to check.
 * @returns {boolean} True if the level is valid, false otherwise.
 * @memberof LogLevel
 * @static
 */
function hasLevel (level) {
  return Value2Name.has(level)
}

/**
 * Checks if the given level name exists in the Name enum/object.
 * @param {string} levelName - The name of the log level to check.
 * @returns {boolean} True if the level name exists, false otherwise.
 * @memberof LogLevel
 * @static
 */
function hasName (levelName) {
  if (!isString(levelName)) {
    return false
  }
  return Names.has(levelName.toUpperCase())
}

/**
 * Validates that a given level is a number and falls within the valid range.
 * @param {*} level - The log level to validate
 * @throws {Error} If level is not a number or outside valid range (Level.OFF to Level.ALL)
 * @memberof LogLevel
 * @static
 */
function assertLevel (level) {
  if (!isNumber(level)) {
    throw new Error(`Level Not Number: type=${typeof level} value=${level}`)
  }
  if (level < Level.OFF || level > Level.ALL) {
    throw new Error(`Not Valid Level: ${level}, Expect between ${Level.OFF} and ${Level.ALL}`)
  }
}

const LogLevel = {
  Name,
  Level,
  DefaultLevel,
  hasLevel,
  hasName,
  assertLevel,
  /**
   *  Same with [Name]{@link LogLevel.Name}
   *  @type {Object}
   *  @memberof LogLevel
   *  @static
   */
  LevelName: Name,
  /**
   *  Same with [Level]{@link LogLevel.Level}
   *  @type {Object}
   *  @memberof LogLevel
   *  @static
   */
  LevelValue: Level,

  value2Name,

  name2Value
}

export default LogLevel

export {
  Name,
  Level,
  DefaultLevel,
  hasLevel,
  hasName,
  assertLevel,
  Name as LevelName,
  Level as LevelValue,
  value2Name,
  name2Value
}
